import { useEffect, useState, useCallback, useRef } from 'react'
import { supabase } from '../services/supabase'
import { useEmpresaContext } from './useEmpresaContext'

interface ParcelaNotificacao {
  id: string
  lancamento_id: string
  numero_parcela: number
  valor: number
  data_vencimento: string
  situacao: string
  tipo: 'PAGAR' | 'RECEBER'
  descricao: string
  pessoa_nome: string | null
  dias_atraso: number
}

interface ContratoAVencer {
  id: string
  numero: number
  titulo: string
  pessoa_nome: string | null
  data_fim: string
  dias_restantes: number
}

interface CaixaAbertoDiaAnterior {
  id: string
  terminal_nome: string
  data_caixa: string
}

export interface NotificacoesAgrupadas {
  vencidas_pagar: ParcelaNotificacao[]
  vencidas_receber: ParcelaNotificacao[]
  vence_hoje_pagar: ParcelaNotificacao[]
  vence_hoje_receber: ParcelaNotificacao[]
  boletos_nao_gerados: ParcelaNotificacao[]
  caixas_abertos_dia_anterior: CaixaAbertoDiaAnterior[]
  contratos_a_vencer: ContratoAVencer[]
}

interface UseNotificacoesFinanceirasReturn {
  notificacoes: NotificacoesAgrupadas
  totalCount: number
  loading: boolean
  refetch: () => void
}

const EMPTY_NOTIFICACOES: NotificacoesAgrupadas = {
  vencidas_pagar: [],
  vencidas_receber: [],
  vence_hoje_pagar: [],
  vence_hoje_receber: [],
  boletos_nao_gerados: [],
  caixas_abertos_dia_anterior: [],
  contratos_a_vencer: [],
}

export function useNotificacoesFinanceiras(): UseNotificacoesFinanceirasReturn {
  const { getEmpresaId } = useEmpresaContext()
  const [notificacoes, setNotificacoes] = useState<NotificacoesAgrupadas>(EMPTY_NOTIFICACOES)
  const [loading, setLoading] = useState(true)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const fetchNotificacoes = useCallback(async () => {
    try {
      const empresaId = await getEmpresaId()
      if (!empresaId) {
        setNotificacoes(EMPTY_NOTIFICACOES)
        setLoading(false)
        return
      }

      const hoje = new Date().toISOString().split('T')[0]

      const { data: parcelas, error } = await supabase
        .from('lancamentos_parcelas')
        .select(`
          id,
          lancamento_id,
          numero_parcela,
          valor,
          data_vencimento,
          situacao,
          lancamentos!inner (
            tipo,
            descricao,
            empresa_id,
            pessoa_id,
            pessoas (
              nome
            )
          )
        `)
        .in('situacao', ['ABERTO', 'PARCIAL'])
        .lte('data_vencimento', hoje)
        .eq('lancamentos.empresa_id', empresaId)
        .order('data_vencimento', { ascending: true })
        .limit(50)

      if (error) {
        console.error('Erro ao buscar notificações financeiras:', error)
        setNotificacoes(EMPTY_NOTIFICACOES)
        setLoading(false)
        return
      }

      // Boletos ainda não gerados: parcelas RECEBER em aberto, forma BOLETO com carteira, sem payment_id
      const { data: boletosNaoGerados, error: errBoletos } = await supabase
        .from('lancamentos_parcelas')
        .select(`
          id,
          lancamento_id,
          numero_parcela,
          valor,
          data_vencimento,
          situacao,
          payment_id,
          linha_digitavel,
          lancamentos!inner (
            tipo,
            descricao,
            empresa_id,
            carteira_cobranca_id,
            pessoas (
              nome
            ),
            formas_pagamento (
              tipo
            )
          )
        `)
        .in('situacao', ['ABERTO', 'PARCIAL'])
        .eq('lancamentos.tipo', 'RECEBER')
        .eq('lancamentos.empresa_id', empresaId)
        .not('lancamentos.carteira_cobranca_id', 'is', null)
        .lte('data_vencimento', hoje)
        .order('data_vencimento', { ascending: true })
        .limit(100)
      if (errBoletos) console.error('Erro ao buscar boletos não gerados:', errBoletos)

      // Contratos ativos que vencem dentro do aviso previo configurado
      const { data: contratosVencendo, error: errContratos } = await supabase
        .from('contratos')
        .select('id, numero, titulo, data_fim, alerta_dias_antes, pessoas(nome)')
        .eq('empresa_id', empresaId)
        .eq('status', 'ATIVO')
        .not('data_fim', 'is', null)
        .gte('data_fim', hoje)
        .order('data_fim', { ascending: true })
        .limit(50)
      if (errContratos) console.error('Erro ao buscar contratos a vencer:', errContratos)

      const { data: caixasAbertos } = await supabase
        .from('caixa_aberturas')
        .select('id, data_caixa, caixa_terminais(nome)')
        .eq('empresa_id', empresaId)
        .eq('status', 'ABERTO')
        .lt('data_caixa', hoje)
        .order('data_caixa', { ascending: true })

      const caixasAbertosAnteriores: CaixaAbertoDiaAnterior[] = (caixasAbertos || []).map((c) => {
        const terminal = c.caixa_terminais as unknown as { nome: string } | null
        return {
          id: c.id,
          terminal_nome: terminal?.nome || 'Terminal',
          data_caixa: c.data_caixa,
        }
      })

      const msPorDia = 1000 * 60 * 60 * 24
      const contratosAVencer: ContratoAVencer[] = (contratosVencendo || []).reduce<ContratoAVencer[]>((lista, c) => {
        const diasRestantes = Math.floor(
          (new Date(`${c.data_fim}T00:00:00`).getTime() - new Date(`${hoje}T00:00:00`).getTime()) / msPorDia
        )
        if (diasRestantes > (c.alerta_dias_antes || 30)) return lista

        const pessoa = c.pessoas as unknown as { nome: string } | null
        lista.push({
          id: c.id,
          numero: c.numero,
          titulo: c.titulo,
          pessoa_nome: pessoa?.nome ?? null,
          data_fim: c.data_fim,
          dias_restantes: diasRestantes
        })
        return lista
      }, [])

      const resultado: NotificacoesAgrupadas = {
        vencidas_pagar: [],
        vencidas_receber: [],
        vence_hoje_pagar: [],
        vence_hoje_receber: [],
        boletos_nao_gerados: [],
        caixas_abertos_dia_anterior: caixasAbertosAnteriores,
        contratos_a_vencer: contratosAVencer,
      }

      if (boletosNaoGerados) {
        for (const p of boletosNaoGerados) {
          // payment_id pode vir null ou string vazia quando o boleto ainda não foi gerado
          if (p.payment_id || p.linha_digitavel) continue
          const lanc = p.lancamentos as unknown as {
            descricao: string | null
            pessoas: { nome: string } | null
            formas_pagamento: { tipo: string } | null
          }
          if (lanc.formas_pagamento?.tipo !== 'BOLETO') continue
          const diffMs = new Date(hoje).getTime() - new Date(p.data_vencimento).getTime()
          const diasAtraso = Math.floor(diffMs / (1000 * 60 * 60 * 24))
          resultado.boletos_nao_gerados.push({
            id: p.id,
            lancamento_id: p.lancamento_id,
            numero_parcela: p.numero_parcela,
            valor: Number(p.valor),
            data_vencimento: p.data_vencimento,
            situacao: p.situacao ?? 'ABERTO',
            tipo: 'RECEBER',
            descricao: lanc.descricao || '',
            pessoa_nome: lanc.pessoas?.nome ?? null,
            dias_atraso: diasAtraso,
          })
        }
      }

      if (parcelas) {
        for (const p of parcelas) {
          const lanc = p.lancamentos as unknown as {
            tipo: 'PAGAR' | 'RECEBER'
            descricao: string | null
            empresa_id: string
            pessoa_id: string | null
            pessoas: { nome: string } | null
          }
          const tipo = lanc.tipo
          const descricao = lanc.descricao || ''
          const pessoa = lanc.pessoas

          const diffMs = new Date(hoje).getTime() - new Date(p.data_vencimento).getTime()
          const diasAtraso = Math.floor(diffMs / (1000 * 60 * 60 * 24))

          const item: ParcelaNotificacao = {
            id: p.id,
            lancamento_id: p.lancamento_id,
            numero_parcela: p.numero_parcela,
            valor: Number(p.valor),
            data_vencimento: p.data_vencimento,
            situacao: p.situacao ?? 'ABERTO',
            tipo,
            descricao,
            pessoa_nome: pessoa?.nome ?? null,
            dias_atraso: diasAtraso,
          }

          if (diasAtraso === 0) {
            if (tipo === 'PAGAR') resultado.vence_hoje_pagar.push(item)
            else resultado.vence_hoje_receber.push(item)
          } else {
            if (tipo === 'PAGAR') resultado.vencidas_pagar.push(item)
            else resultado.vencidas_receber.push(item)
          }
        }
      }

      setNotificacoes(resultado)
    } catch (err) {
      console.error('Erro ao buscar notificações financeiras:', err)
      setNotificacoes(EMPTY_NOTIFICACOES)
    } finally {
      setLoading(false)
    }
  }, [getEmpresaId])

  // Fetch inicial + polling a cada 5 minutos
  useEffect(() => {
    fetchNotificacoes()

    intervalRef.current = setInterval(fetchNotificacoes, 5 * 60 * 1000)

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [fetchNotificacoes])

  const totalCount =
    notificacoes.vencidas_pagar.length +
    notificacoes.vencidas_receber.length +
    notificacoes.vence_hoje_pagar.length +
    notificacoes.vence_hoje_receber.length +
    notificacoes.boletos_nao_gerados.length +
    notificacoes.caixas_abertos_dia_anterior.length +
    notificacoes.contratos_a_vencer.length

  return {
    notificacoes,
    totalCount,
    loading,
    refetch: fetchNotificacoes,
  }
}
